/**
 * @module feedback
 */

import $ from 'jquery';

import { addClass, removeClass } from './common/domHelpers.es6';
import Form from './common/form';
import MAEvent from './common/ma-event';
import { initializePlacesTypeahead } from './common/search';
import { thorPushEvent } from './common/thor';

require('jquery-form')();
require('jquery.textchange');
require('raty-js');

/**
 * Save draft data via Ajax
 * @param {jQuery} $form
 * @private
 */
function _saveFeedbackFormDraft($form) {
    const data = $form.serialize();
    const url = $form.data('action-draft');

    $form.trigger(MAEvent.formPendingStateChange, { pending: true });
    $.post(url, data).done(response => {
        $form.trigger(MAEvent.formPendingStateChange, { pending: false });
        $form.find('[data-feedback-draft-id]').val(response.feedback_draft_id);
    });
}

/**
 * @param {jQuery} $form Feedback form instance
 * @private
 */
function _gotoFormStep2($form) {
    $form.data('current-step', 2);
    $form.find('[data-feedback-form-step]').addClass('hide');
    $form.find('[data-feedback-form-step=2]').removeClass('hide');
    // force status: user can go from step 3 back to step 2
    $form.find('[data-status]').val('FEEDBACK_DRAFT_STATUSES.FIRST_STEP_DONE');
}

/**
 * @param {jQuery} $form Feedback form instance
 * @private
 */
function _gotoFormStep3($form) {
    $form.data('current-step', 3);
    $form.find('[data-feedback-form-step]').addClass('hide');
    $form.find('[data-feedback-form-step=3]').removeClass('hide');
    // Remove error on code field
    Form.displayErrors($form, {});
}

/**
 * @param {jQuery} $form Feedback form instance
 * @private
 */
function _gotoFormStep4($form) {
    $form.data('current-step', 4);
    $form.find('[data-feedback-form-step]').addClass('hide');
    $form.find('[data-feedback-form-step=4]').removeClass('hide');
    $('[data-popin-body]').addClass('popin--reference');
}

/**
 * Display a preview of the name as it will be display in website
 * @param {jQuery} $form
 * @param {jQuery} $firstNameField
 * @param {jQuery} $lastNameField
 * @private
 */
function _updateDisplayName($form, $firstNameField, $lastNameField) {
    const $displayName = $form.find('[data-display-name]');
    if ($firstNameField.val() && $lastNameField.val()) {
        $displayName.html(`${$firstNameField.val()} ${$lastNameField.val().substring(0, 1)}.`);
        $displayName.parent().removeClass('invisible');
    } else {
        $displayName.parent().addClass('invisible');
    }
}
/**
 * Post data via Ajax
 * @param {jQuery} $form
 * @private
 */
function _postFeedbackFormSMSCode($form) {
    const data = $form.serialize();
    const url = $form.attr('action');

    $form.trigger(MAEvent.formPendingStateChange, { pending: true });
    $.post(url, data)
        .done(() => {
            // Form is valid, code is ok
            _gotoFormStep4($form);
        })
        .fail(() => {
            // Code is incorrect
            Form.displayErrors($form, {
                code: [$form.find('[data-code]').data('validation-error')],
            });
            Form.scrollToFirstError($form, $form.parents('.mfp-wrap'));
        })
        .always(() => {
            $form.trigger(MAEvent.formPendingStateChange, { pending: false });
        });
}

/**
 * Post data via Ajax
 * @param {jQuery} $form
 * @param {Function} doneCallback
 * @private
 */
function _validateFeedbackFormDraft($form, doneCallback) {
    const data = $form.serialize();
    let url;

    if ($form.data('current-step') === 1) {
        url = $form.data('action-step1');
    } else if ($form.data('current-step') === 2) {
        url = $form.data('action-step2');
    } else {
        return;
    }

    $form.trigger(MAEvent.formPendingStateChange, { pending: true });
    $.post(url, data)
        .done(() => {
            if (typeof doneCallback === 'function') {
                doneCallback($form);
            }
        })
        .always(xhr => {
            const $certifyField = $form.find('[data-certify]');

            Form.displayErrors($form, xhr.responseJSON);
            Form.scrollToFirstError($form, $form.parents('.mfp-wrap'));
            $form.trigger(MAEvent.formPendingStateChange, { pending: false });
            // Validate the certify checkbox
            if (!$certifyField.prop('checked')) {
                Form.setFieldError($certifyField, [$certifyField.data('validation-error')]);
            } else {
                Form.setFieldError($certifyField, null);
            }
        });
}

/**
 * Post data via Ajax
 * @param {jQuery} $form
 * @private
 */
function _sendFeedbackFormCode($form) {
    const url = $form.data('action-draft');
    const data = $form.serialize();

    $form.trigger(MAEvent.formPendingStateChange, { pending: true });
    $.post(url, data)
        .done(response => {
            const formDatas = $form.serializeArray();
            const realtorId = formDatas.find(item => item.name === 'realtor_id').value;
            const rating = formDatas.find(item => item.name === 'rating_recommandation').value;
            const eventArgs = {
                av_category: 'find_pro',
                av_items: [
                    {
                        id: realtorId,
                        rating,
                    },
                ],
                av_zone: 'top',
            };
            thorPushEvent('rating_agency_form_confirm', eventArgs);

            $form.trigger(MAEvent.formPendingStateChange, { pending: false });
            // SMS code successfully sent, goto step2
            $form.find('[data-signature]').val(response.signature);
            $form.find('[data-phone-number]').html(response.contact_phone_number);
            _gotoFormStep3($form);
        })
        .fail(() => {
            Form.displayErrors($form, { code: [$form.find('[data-code]').data('send-error')] });
        });
}

export function feedbackDraft($content) {
    const $form = $content.find('form').eq(0);
    const form = $form[0];
    const $statusField = $form.find('[data-status]');
    const $firstNameField = $form.find('[data-contact-firstname]');
    const $lastNameField = $form.find('[data-contact-lastname]');
    const $certifyField = $form.find('[data-certify]');

    const $moreTermLink = $content.find('#more-term-link');
    const $moreTerm = $content.find('#more-term');
    const $arrowTermLink = $moreTermLink.find('[class^="icon-"]');

    $moreTermLink.on('click', e => {
        e.preventDefault();
        $moreTerm.toggleClass('mfp-hide');
        $arrowTermLink.toggleClass('icon-arrow-down icon-arrow-up');
    });

    $form.data('current-step', 1);

    if ($statusField.val() === 'FEEDBACK_DRAFT_STATUSES.EMPTY') {
        $statusField.val('FEEDBACK_DRAFT_STATUSES.LANDED');
        _saveFeedbackFormDraft($form);
    }

    // Initialize rating controls
    $form.find('[data-raty]').each(function formDataRaty() {
        const $this = $(this);

        const ratyOptions = {
            starType: 'i',
            scoreName: $this.data('name'),
            starOff: 'rating__star svg-sprite svg-sprite-star--empty',
            starHalf: 'rating__star svg-sprite svg-sprite-star--half',
            starOn: 'rating__star svg-sprite svg-sprite-star--full',
            hints: $this.data('raty-hints').split(','),
            click(score) {
                // Raty update the hidden field after the click event so the value is not set when we trigger the change event
                // Hack : set the score manually before trigger event
                $form
                    .find(`[name=${$this.data('name')}]`)
                    .val(score)
                    .trigger('change');
            },
        };

        if ($this.data('raty-target')) {
            ratyOptions.targetText = 'Choisissez';
            ratyOptions.target = `[data-raty-target=${$this.data('name')}]`;
            ratyOptions.targetKeep = true;
        }

        // Specific behavior for main rating :
        // if the user change this value, populate all sub-rating with same value (if field is empty)
        if ($this.data('name') === 'rating_recommandation') {
            ratyOptions.click = function onRatyClick(score) {
                $([
                    'rating_welcoming',
                    'rating_quality',
                    'rating_skill',
                    'rating_followup',
                    'rating_value_for_money',
                ]).each(function ratingEach() {
                    const $field = $form.find(`[name=${this}]`);

                    const $raty = $form.find(`[data-name=${this}]`);
                    if ($field.val() === '') {
                        $raty.data('raty').score(score);
                    }
                });
                $form
                    .find(`[name=${$this.data('name')}]`)
                    .val(score)
                    .trigger('change');
            };
        }

        $this.raty(ratyOptions);
    });

    // Specific behavior for item type :
    // If the user selects land, number of rooms field is hidden as not applicable
    form.querySelector('[data-item-type-select]').addEventListener('change', event => {
        const itemTypeField = event.target;
        const roomsField = itemTypeField.form.querySelector('[data-rooms]');
        if (itemTypeField.value === 'ITEM_TYPE.LAND') {
            addClass(roomsField, 'hide');
        } else {
            removeClass(roomsField, 'hide');
        }
    });

    // Initialize place field
    initializePlacesTypeahead($form.find('[data-place-field]'));
    $form.find('[data-place-field]').on(MAEvent.placeSelected, () => {
        _saveFeedbackFormDraft($form);
    });

    // Bind change event on all fields except code field to save data
    $form.find(':input:not([name=code]):not([name=place])').on('change', () => {
        _saveFeedbackFormDraft($form);
    });

    // Back to step 2 to change phone number
    $form.find('[data-backto-mobile-step]').on('click', e => {
        e.preventDefault();
        _gotoFormStep2($form);
    });

    // When name change give a preview of the dislay name to the user
    $firstNameField.on(
        'textchange',
        _updateDisplayName.bind(null, $form, $firstNameField, $lastNameField),
    );
    $lastNameField.on(
        'textchange',
        _updateDisplayName.bind(null, $form, $firstNameField, $lastNameField),
    );
    _updateDisplayName($form, $firstNameField, $lastNameField); // contact info pre-filled if user logged-in

    // Disable button when AJAX call are pending
    $form.on(MAEvent.formPendingStateChange, event => {
        $form.find('button').prop('disabled', event.pending);
    });

    // Submit form step by step
    $form.on('submit', e => {
        e.preventDefault();

        switch ($form.data('current-step')) {
            case 1:
                $form.find('[data-status]').val('FEEDBACK_DRAFT_STATUSES.DRAFT');
                _validateFeedbackFormDraft($form, () => {
                    if ($certifyField.prop('checked')) {
                        $form.find('[data-status]').val('FEEDBACK_DRAFT_STATUSES.FIRST_STEP_DONE');
                        _saveFeedbackFormDraft($form);
                        _gotoFormStep2($form);
                    }
                });
                break;
            case 2:
                _validateFeedbackFormDraft($form, () => {
                    $form.find('[data-status]').val('FEEDBACK_DRAFT_STATUSES.SMS_SENT');
                    _sendFeedbackFormCode($form);
                });
                break;
            case 3:
                _postFeedbackFormSMSCode($form);
                break;
            default:
                break;
        }
    });
}
